import { parseInteractionBlueprint } from "@/lib/contentParsers/interactionBlueprint";
import { buildSearchIndexRow } from "../build/buildSearchIndexRow";
import { fetchBlueprintResolvedOnce } from "../../../blueprint/blueprintFetch";
import { isSparxBlueprintNotFoundError } from "../../../blueprint/sparxBlueprintNotFoundError";
import type { AppStore } from "@/store/app/storeTypes";
import type { SparxsBlueprintFetchResult } from "@/types/sparxsBlueprintFetchResult";
import type { SearchIndexRow } from "../../types/searchTypes";

/** Human-readable message for thrown/caught values when recording index failures. */
const toIndexPassErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message !== "") {
    return error.message;
  }
  if (typeof error === "string" && error !== "") {
    return error;
  }
  return fallback;
};

/** Matches prior network-queue failure shaping: optional message when nothing usable. */
export const toNetworkFetchFailureMessage = (
  lastError: unknown,
): string | undefined => {
  if (lastError instanceof Error && lastError.message !== "") {
    return lastError.message;
  }
  if (typeof lastError === "string" && lastError !== "") {
    return lastError;
  }
  if (lastError != null) {
    return "Request failed";
  }
  return undefined;
};

export type TryBuildSearchIndexRowResult =
  | { ok: true; row: SearchIndexRow }
  | { ok: false; message: string };

export const tryBuildSearchIndexRowFromBlueprint = (params: {
  blueprint: unknown;
  fileName: string;
  title: string;
  key: string;
  listFingerprint: string;
  listLastModified?: number;
}): TryBuildSearchIndexRowResult => {
  try {
    const row = buildSearchIndexRow(
      parseInteractionBlueprint(params.blueprint),
      {
        fileName: params.fileName,
        title: params.title,
        key: params.key,
        listFingerprint: params.listFingerprint,
        listLastModified: params.listLastModified,
      },
    );
    return { ok: true, row };
  } catch (error: unknown) {
    return {
      ok: false,
      message: toIndexPassErrorMessage(error, "Cached blueprint parse failed"),
    };
  }
};

export type IndexPassFetchOutcome =
  | { kind: "ok"; resolved: SparxsBlueprintFetchResult }
  | { kind: "failed"; lastError: unknown }
  | { kind: "aborted" };

export const fetchBlueprintForIndexPass = async (params: {
  fileName: string;
  store: AppStore;
  signal: AbortSignal;
  maxAttempts: number;
  sleepMs: (ms: number) => Promise<void>;
}): Promise<IndexPassFetchOutcome> => {
  const { fileName, store, signal, maxAttempts, sleepMs } = params;
  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal.aborted) {
      return { kind: "aborted" };
    }
    try {
      const resolved = await fetchBlueprintResolvedOnce({
        fileName,
        getState: () => store.getState(),
        dispatch: store.dispatch,
        signal,
      });
      return { kind: "ok", resolved };
    } catch (error: unknown) {
      lastError = error;
      if (error instanceof Error && error.name === "AbortError") {
        return { kind: "aborted" };
      }
      if (isSparxBlueprintNotFoundError(error)) {
        return { kind: "failed", lastError };
      }
      if (attempt < maxAttempts - 1) {
        await sleepMs(400 * 2 ** attempt);
      }
    }
  }
  return { kind: "failed", lastError };
};
